// v4.10 - TRUST-KPI-01: per-card "Answer Confidence" badge - 2026-04-28
//
// Renders a small MUI Chip with a Tooltip that explains *why* the answer
// has the confidence it does. Distinct from DataReliabilityBadge (which
// is tenant-wide). Intentionally tiny — the only logic is tier→colour
// mapping and override short-circuit.
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import type { CardConfidence } from '../services/api';

type ChipColor = 'success' | 'info' | 'warning' | 'error' | 'default';

const TIER_COLOR: Record<CardConfidence['tier'], ChipColor> = {
  high_confidence: 'success',
  likely_correct: 'info',
  needs_review: 'warning',
  conflicting_records: 'error',
  insufficient_data: 'default',
};

const TIER_TOOLTIP: Record<CardConfidence['tier'], string> = {
  high_confidence:
    'Multiple reliable sources agree on this number.',
  likely_correct:
    'Most reliable sources agree, with minor differences.',
  needs_review:
    'Sources show some disagreement. Worth a second look.',
  conflicting_records:
    'Sources strongly disagree on this number — verify before acting.',
  insufficient_data:
    'Not enough reliable data to be sure of this number yet.',
};

const OVERRIDE_TOOLTIP =
  "You manually set this value, so we're showing it as-is — trust signals were skipped.";

export interface ConfidenceBadgeProps {
  confidence: CardConfidence | null | undefined;
}

const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ confidence }) => {
  if (!confidence) return null;

  if (confidence.override === 'user_override') {
    return (
      <Tooltip title={OVERRIDE_TOOLTIP} arrow>
        <Chip
          size="small"
          label="Manual override"
          color="default"
          icon={<VerifiedRoundedIcon fontSize="small" />}
          sx={{ fontWeight: 500 }}
        />
      </Tooltip>
    );
  }

  const color = TIER_COLOR[confidence.tier] || 'default';
  const tip = TIER_TOOLTIP[confidence.tier] || '';
  const Icon =
    color === 'success'
      ? VerifiedRoundedIcon
      : color === 'error'
      ? ErrorOutlineRoundedIcon
      : HelpOutlineRoundedIcon;

  return (
    <Tooltip title={tip} arrow>
      <Chip
        size="small"
        label={confidence.label}
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        icon={<Icon fontSize="small" />}
        sx={{ fontWeight: 500 }}
        aria-label={`Answer confidence: ${confidence.label}`}
      />
    </Tooltip>
  );
};

export default ConfidenceBadge;
